/**
 * Payment Plans page (US 4396) — every active payment plan for an agency in one place:
 * total owed, next payment, past due, the schedule, payment history, and paying (including
 * paying ahead). Payments go through the plan's batches with the same checkout as Batches
 * to Pay, so the server feeds them back into the plan exactly as before.
 *
 * Viewing needs READ:AR on the agency (same gate as the plan cards in Batches to Pay);
 * paying additionally needs READ:ELECTRONIC-PAYMENT and READ:EXECUTION-REPORT (what the
 * pay-batches endpoint checks).
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { BanknotesIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import {
  OathPageWrapper,
  OathHeaderCard,
  OathEmptyState,
  OathAlert,
  OathSpinner,
  OathAgencySelector,
  OathButton,
} from '../../shared/OathUI';
import { withErrorBoundary } from '../powerreporting/components/shared';
import { useOathAuth } from '../../../services/oathAuthContext';
import usePermissions from '../../../hooks/usePermissions';
import { getPaymentPlans, getPlansToSign } from '../../../services/oathPaymentPlanService';
import { formatCurrencyWithSymbol } from '../powerreporting/utils/formatters';
import BatchPaymentModal from '../powerreporting/modals/BatchPaymentModal';
import PdfViewerModal from '../powerreporting/modals/PdfViewerModal';
import RequestAchInfoPrompt from '../powerreporting/components/RequestAchInfoPrompt';
import PlanCard from './components/PlanCard';
import PayPlanModal from './modals/PayPlanModal';
import SignLetterModal from './modals/SignLetterModal';
import PlansToSign from './components/PlansToSign';
import { allocatePlanPayment } from './utils/planPayment';

const PaymentPlansPage = () => {
  const { user } = useOathAuth();
  const { canInAgency, canAnyInAgency } = usePermissions();

  // Only agencies the user can see balances for.
  const agencies = useMemo(
    () => (user?.agencies || []).filter((a) => canInAgency(a.agencyId, 'READ:AR')),
    [user?.agencies, canInAgency]
  );

  const [selectedAgencyId, setSelectedAgencyId] = useState(null);
  const [plans, setPlans] = useState([]);
  const [plansToSign, setPlansToSign] = useState([]);
  const [signingPlan, setSigningPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [payingPlan, setPayingPlan] = useState(null);
  const [checkout, setCheckout] = useState(null); // { selectedBatches, paymentAmounts, batches }
  const [receipt, setReceipt] = useState(null); // { pdf, number }

  // Default to the main agency, like Power Reporting.
  useEffect(() => {
    if (agencies.length > 0 && !agencies.some((a) => a.agencyId === selectedAgencyId)) {
      const main = agencies.find((a) => a.isMainAgency);
      setSelectedAgencyId((main || agencies[0]).agencyId);
    }
  }, [agencies, selectedAgencyId]);

  // Drop responses for an agency the user has already switched away from.
  const requestSeq = useRef(0);
  const fetchPlans = useCallback(async () => {
    if (!selectedAgencyId) return;
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    // Letters to sign are best-effort: a failure there must not hide the plans.
    const [result, toSign] = await Promise.all([
      // This page pays plans, so it needs each plan's payable batches attached.
      getPaymentPlans(selectedAgencyId, { includePayableBatches: true }),
      getPlansToSign(selectedAgencyId),
    ]);
    if (seq !== requestSeq.current) return;
    setLoading(false);
    setPlansToSign(toSign.success ? toSign.data || [] : []);
    if (result.success) {
      setPlans(result.data || []);
    } else {
      setPlans([]);
      setError(result.forbidden
        ? "You don't have access to this agency's payment plans."
        : result.error || 'Failed to load payment plans.');
    }
  }, [selectedAgencyId]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  // Same slugs pay-batches accepts: the sub-agency variant lets a GA user pay a sub-agency's plans.
  const canPay = !!selectedAgencyId
    && canAnyInAgency(selectedAgencyId, ['READ:ELECTRONIC-PAYMENT', 'READ:ELECTRONIC-PAYMENT:SUB-AGENCY'])
    && canInAgency(selectedAgencyId, 'READ:EXECUTION-REPORT');

  const openPlans = useMemo(() => plans.filter((p) => !p.isPaidOff), [plans]);
  const totals = useMemo(() => ({
    owed: openPlans.reduce((s, p) => s + (Number(p.remainingBalance) || 0), 0),
    pastDue: openPlans.reduce((s, p) => s + (Number(p.pastDueAmount) || 0), 0),
  }), [openPlans]);

  const handleContinueToPayment = (amount) => {
    const allocation = allocatePlanPayment(payingPlan, amount);
    setPayingPlan(null);
    if (allocation.selectedBatches.size === 0) {
      toast.error('Nothing on this plan can be paid online right now.');
      return;
    }
    setCheckout(allocation);
  };

  const handlePaymentSuccess = (response) => {
    if (response?.pdfBase64) {
      setReceipt({ pdf: response.pdfBase64, number: response.receiptNumber || 'Payment Receipt' });
    }
    fetchPlans();
  };

  const subtitle = openPlans.length > 0
    ? `${formatCurrencyWithSymbol(totals.owed)} owed across ${openPlans.length} plan${openPlans.length === 1 ? '' : 's'}`
    : 'Your agency payment plans';

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center min-h-[300px]">
          <OathSpinner size="lg" />
        </div>
      );
    }

    if (error) {
      return (
        <div className="space-y-4">
          <OathAlert variant="error" message={error} />
          <div className="flex justify-center">
            <OathButton onClick={fetchPlans} variant="secondary">
              <ArrowPathIcon className="h-4 w-4 mr-2" />
              Retry
            </OathButton>
          </div>
        </div>
      );
    }

    if (plans.length === 0 && plansToSign.length === 0) {
      return (
        <OathEmptyState
          icon={BanknotesIcon}
          title="No Payment Plans"
          message="This agency has no active payment plans."
        />
      );
    }

    return (
      <div className="space-y-4">
        <PlansToSign plans={plansToSign} onSign={setSigningPlan} />

        {totals.pastDue > 0 && (
          <OathAlert
            variant="warning"
            message={`${formatCurrencyWithSymbol(totals.pastDue)} is past due across your payment plans.`}
          />
        )}

        {!canPay && openPlans.length > 0 && (
          <RequestAchInfoPrompt agencyId={selectedAgencyId} layout="banner" />
        )}

        {plans.map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            canPay={canPay}
            onPay={setPayingPlan}
            allowVoid={canInAgency(selectedAgencyId, 'READ:ELECTRONIC-PAYMENT')}
            onVoided={fetchPlans}
          />
        ))}
      </div>
    );
  };

  return (
    <OathPageWrapper maxWidth="max-w-5xl" responsive>
      <OathHeaderCard
        icon={BanknotesIcon}
        title="Payment Plans"
        subtitle={subtitle}
        rightContent={agencies.length > 1 ? (
          <OathAgencySelector
            agencies={agencies}
            selectedAgencyId={selectedAgencyId}
            onSelect={setSelectedAgencyId}
          />
        ) : agencies.length === 1 ? (
          <span className="text-sm qhd:text-base text-gray-500 dark:text-gray-400">
            {agencies[0].dbaAgencyName} <span className="text-xs text-gray-400 dark:text-gray-500">#{agencies[0].agencyId}</span>
          </span>
        ) : undefined}
      />

      {renderContent()}

      <SignLetterModal
        isOpen={!!signingPlan}
        agencyId={selectedAgencyId}
        plan={signingPlan}
        onClose={() => setSigningPlan(null)}
        onSigned={fetchPlans}
      />

      <PayPlanModal
        isOpen={!!payingPlan}
        plan={payingPlan}
        onClose={() => setPayingPlan(null)}
        onContinue={handleContinueToPayment}
      />

      <BatchPaymentModal
        isOpen={!!checkout}
        onClose={() => setCheckout(null)}
        selectedBatches={checkout?.selectedBatches || new Set()}
        paymentAmounts={checkout?.paymentAmounts || {}}
        batches={checkout?.batches || []}
        agencyId={selectedAgencyId}
        onSuccess={handlePaymentSuccess}
      />

      <PdfViewerModal
        isOpen={!!receipt}
        onClose={() => setReceipt(null)}
        pdfData={receipt?.pdf}
        reportNumber={receipt?.number}
        title={`Payment Receipt: ${receipt?.number || ''}`}
      />
    </OathPageWrapper>
  );
};

export default withErrorBoundary(PaymentPlansPage, {
  title: 'Payment Plans Error',
  message: 'An error occurred loading your payment plans. Please try again.'
});
